import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import FileUploadZone from "@/components/FileUploadZone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { enrichLabelWithYear } from "@/lib/exerciseYear";
import { Stethoscope, ChevronRight } from "lucide-react";
import GeoLocationInput from "@/components/GeoLocationInput";
import DichiarazioniBlock from "@/components/DichiarazioniBlock";
import ClaimsInfoSheet, { emptyClaim } from "@/components/ClaimsInfoSheet";
import { StepProgress, FormNavigation } from "@/components/StepProgress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "odontoiatra", label: "Dentisti e Odontoiatri", desc: "Assicurazione RC per chi esercita la professione odontoiatrica" },
  { id: "medico_pubblico", label: "Medici Dipendenti (Strutture Pubbliche)", desc: "Copertura per medici che lavorano nel servizio sanitario pubblico" },
  { id: "libero_professionista", label: "Medici Liberi Professionisti", desc: "RC per medici con attività privata" },
  { id: "medico_privato", label: "Medici Dipendenti (Strutture Private)", desc: "Copertura per medici impiegati in cliniche e strutture private" },
  { id: "medico_generale", label: "Medici di Base e Pediatri SSN", desc: "Assicurazione RC per medici di medicina generale e pediatri convenzionati" },
  { id: "personale_non_medico", label: "Professioni Sanitarie Non Mediche", desc: "Copertura per infermieri, ostetriche, tecnici sanitari e simili" },
  { id: "specializzando", label: "Medici in Formazione (Specializzandi)", desc: "RC dedicata a giovani medici e specializzandi" },
  { id: "struttura", label: "Strutture Sanitarie e Cliniche", desc: "Polizze per ospedali, poliambulatori e strutture sanitarie" },
];

const STEPS = ["Anagrafica", "Assicurazione", "Condizioni", "Estensioni", "Sinistri", "Dichiarazioni"];

const SPEC_NON_INVASIVE = [
  "Allergologia e Immunologia clinica", "Anatomia patologica", "Andrologia", "Angiologia e Flebologia",
  "Audiologia", "Biochimica e chimica clinica", "Biologia", "Cardiologia", "Dermatologia - Venereologia",
  "Diabetologia e malattie del metabolismo", "Ematologia e Medicina Trasfusionale", "Endocrinologia",
  "Epatologia", "Farmacologia", "Fisica Medica", "Genetica Medica", "Geriatria", "Igiene e medicina preventiva",
  "Immunologia", "Malattie dell'apparato cardiovascolare", "Malattie dell'apparato digerente",
  "Malattie dell'apparato respiratorio", "Medicina Nucleare", "Medicina legale", "Medicina interna",
  "Medicina generale", "Medicina fisica e riabilitazione", "Medicina d'urgenza", "Medicina del lavoro",
  "Medicina dello sport", "Medicina Termale", "Microbiologia/Virologia", "Neurologia", "Neurofisiopatologia",
  "Neuropsichiatria", "Oculistica / Oftalmologia", "Oncologia e senologia", "Otorinolaringoiatria",
  "Patologia Clinica", "Pediatria senza neonatologia", "Pneumologia", "Psichiatria", "Psicologia Clinica",
  "Radiodiagnostica (no mammografia)", "Radiodiagnostica con mammografia", "Radioterapia", "Reumatologia",
  "Scienze dell'alimentazione", "Terapia del dolore e cure palliative", "Urologia",
  "Medico abilitato non specializzato", "Altro"
];

const SPEC_INVASIVE = [
  "Anestesia, rianimazione e terapia intensiva", "Andrologia Invasiva", "Angiologia e Flebologia Invasiva",
  "Cardiochirurgia", "Cardiologia Invasiva", "Chirurgia addominale", "Chirurgia d'urgenza e pronto soccorso",
  "Chirurgia della mano", "Chirurgia Generale", "Chirurgia Maxillo Facciale (no estetica)",
  "Chirurgia Maxillo Facciale (con estetica)", "Chirurgia pediatrica",
  "Chirurgia Plastica e Ricostruttiva (con estetica)", "Chirurgia Ricostruttiva", "Chirurgia toracica",
  "Chirurgia vascolare", "Endocrinochirurgia", "Gastroenterologia Invasiva",
  "Ginecologia con Fecondazione assistita (no Ostetricia)", "Ginecologia e Ostetricia Ambulatoriale",
  "Ginecologia e Ostetricia (con Fecondazione assistita)", "Ginecologia (no Ostetricia)",
  "Medicina Estetica", "Nefrologia Invasiva", "Neurochirurgia",
  "Oculistica / Oftalmologia Invasiva", "Oculistica / Oftalmologia (con estetica)",
  "Ortopedia", "Ortopedia e Traumatologia (no spinali)", "Ortopedia e Traumatologia (con spinali)",
  "Otorinolaringoiatria Chirurgo (no estetica)", "Otorinolaringoiatria Chirurgo (con estetica)",
  "Urologia Invasiva", "Altro"
];

const SPEC_ODONTOIATRIA_NON_INV = [
  "Odontoiatria senza implantologia", "Ortodonzia", "Altro (specificare nelle note)"
];
const SPEC_ODONTOIATRIA_INV = [
  "Odontoiatria con implantologia osteointegrata", "Altro (specificare nelle note)"
];

const MASSIMALI = [
  { value: "2m_6m", label: "\u20ac 2.000.000 / 6.000.000" },
  { value: "3m_6m", label: "\u20ac 3.000.000 / 6.000.000" },
  { value: "5m_6m", label: "\u20ac 5.000.000 / 6.000.000" },
];

const RETROATTIVITA = [
  { value: "illimitata", label: "Illimitata" },
  { value: "10_anni", label: "10 anni" },
];

const FRANCHIGIE = [
  { value: "zero", label: "Zero" }, { value: "1500", label: "\u20ac 1.500" },
  { value: "2500", label: "\u20ac 2.500" }, { value: "5000", label: "\u20ac 5.000" },
  { value: "7500", label: "\u20ac 7.500" }, { value: "10000", label: "\u20ac 10.000" },
  { value: "15000", label: "\u20ac 15.000" }, { value: "20000", label: "\u20ac 20.000" },
];

export default function QuestionarioProfessioniSanitariePage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [step, selectedCategory]);

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const showClaimsSheet = formData.sinistri_5_anni === "si" || formData.fatti_risarcimento === "si";

  useEffect(() => {
    if (showClaimsSheet && (!formData.sinistri_claims || formData.sinistri_claims.length === 0)) {
      setFormData(prev => ({ ...prev, sinistri_claims: [emptyClaim()] }));
    }
  }, [showClaimsSheet, formData.sinistri_claims]);

  const handleFileUpload = (docKey, fileData) => {
    setFiles(prev => {
      const next = { ...prev };
      if (fileData) next[docKey] = fileData;
      else delete next[docKey];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!formData.nome || !formData.cognome || !formData.email || !formData.telefono) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (!formData.privacy) {
      toast.error("Devi accettare la privacy policy per procedere");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        type: "sanitario",
        data: { ...formData, categoria: selectedCategory },
        files: Object.values(files),
        contact: { nome: formData.nome, cognome: formData.cognome, email: formData.email, telefono: formData.telefono }
      });
      navigate("/conferma");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSteps = STEPS.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const isOdontoiatra = selectedCategory === "odontoiatra";
  const specNonInv = isOdontoiatra ? SPEC_ODONTOIATRIA_NON_INV : SPEC_NON_INVASIVE;
  const specInv = isOdontoiatra ? SPEC_ODONTOIATRIA_INV : SPEC_INVASIVE;

  // Category selection screen
  if (!selectedCategory) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="sanitario-page">
          <div className="max-w-3xl mx-auto px-6 py-12">
            <div className="text-center mb-10">
              <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
                <Stethoscope className="w-7 h-7 text-[#163321]" />
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#0F1F14] mb-3" data-testid="sanitario-title">
                Polizze RC Professioni Sanitarie
              </h1>
              <p className="text-sm text-[#4A5E51] max-w-lg mx-auto">
                Adeguate al decreto attuativo Gelli (Legge n. 24/2017). Seleziona la tua categoria professionale per compilare il questionario dedicato.
              </p>
            </div>

            <div className="space-y-3">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => cat.id === "struttura" ? navigate("/questionario/struttura-sanitaria") : setSelectedCategory(cat.id)}
                  className="w-full text-left bg-white rounded-2xl border border-[#E5E2DB] p-5 premium-card hover:border-[#163321]/30 transition-all group"
                  data-testid={`category-${cat.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-heading text-base font-medium text-[#0F1F14] mb-1 group-hover:text-[#163321] transition-colors">
                        {cat.label}
                      </h3>
                      <p className="text-xs text-[#4A5E51]">{cat.desc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#4A5E51] group-hover:text-[#163321] group-hover:translate-x-1 transition-all shrink-0 ml-4" />
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-xs text-[#4A5E51]">
                La garanzia "Responsabilit&agrave; solidale" &egrave; sempre inclusa ai sensi della Legge 8 Marzo 2017, n.24
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = CATEGORIES.find(c => c.id === selectedCategory)?.label || "";

  const renderStepContent = () => {
    switch (step) {
      case 0: return renderAnagrafica();
      case 1: return renderAssicurazione();
      case 2: return renderCondizioni();
      case 3: return renderEstensioni();
      case 4: return renderSinistri();
      case 5: return renderDichiarazioni();
      default: return null;
    }
  };

  const renderAnagrafica = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51] mb-2">Inserisci i tuoi dati anagrafici. Ci servono per prepararti un preventivo personalizzato.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldInput label="Nome" required value={formData.nome} onChange={v => update("nome", v)} testId="san-nome" />
        <FieldInput label="Cognome" required value={formData.cognome} onChange={v => update("cognome", v)} testId="san-cognome" />
      </div>
      <GeoLocationInput label="Sede Legale o Residenza" required value={formData.indirizzo} onChange={v => update("indirizzo", v)} testId="san-indirizzo" />
      <FieldInput label="Indirizzo Email" required type="email" value={formData.email} onChange={v => update("email", v)} placeholder="la.tua@email.it" testId="san-email" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldInput label="Codice Fiscale / P.IVA" required value={formData.codice_fiscale} onChange={v => update("codice_fiscale", v.toUpperCase())} testId="san-cf" />
        <FieldInput label="Telefono cellulare" required type="tel" value={formData.telefono} onChange={v => update("telefono", v)} placeholder="+39 333 1234567" testId="san-telefono" />
      </div>
      <FieldInput label="Denominazione Ente di appartenenza" value={formData.denominazione_ente} onChange={v => update("denominazione_ente", v)} testId="san-ente" />
    </div>
  );

  const renderAssicurazione = () => (
    <div className="space-y-5">
      <YesNoField label="Il Proponente è mai stato assicurato per la RC Professionale?" required field="gia_assicurato" formData={formData} update={update} testId="san-gia-assicurato" />

      {formData.gia_assicurato === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <FieldInput label="Assicuratori precedenti" required value={formData.assicuratori_prec} onChange={v => update("assicuratori_prec", v)} testId="san-assicuratori-prec" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldInput label="Data scadenza ultima copertura" type="date" required value={formData.data_scadenza_cop} onChange={v => update("data_scadenza_cop", v)} testId="san-data-scadenza" />
            <FieldInput label="Numero anni copertura continua" type="number" required value={formData.anni_copertura} onChange={v => update("anni_copertura", v)} testId="san-anni-copertura" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldInput label="Franchigia (ultima copertura)" required value={formData.franchigia_prec} onChange={v => update("franchigia_prec", v)} testId="san-franchigia-prec" />
            <FieldInput label="Massimale (ultima copertura)" required value={formData.massimale_prec} onChange={v => update("massimale_prec", v)} testId="san-massimale-prec" />
          </div>
          <FieldInput label="Ultimo Premio Pagato (\u20ac)" type="number" required value={formData.ultimo_premio} onChange={v => update("ultimo_premio", v)} testId="san-ultimo-premio" />
        </div>
      )}

      <YesNoField label="Specializzazione Invasiva / Chirurgica conseguita o esercitata?" required field="spec_invasiva" formData={formData} update={update} testId="san-spec-invasiva" />

      {formData.spec_invasiva === "no" && (
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Specializzazioni NON invasive / NON chirurgiche</Label>
          <Select value={formData.spec_non_inv || ""} onValueChange={v => update("spec_non_inv", v)}>
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="san-spec-non-inv"><SelectValue placeholder="Seleziona specializzazione" /></SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {specNonInv.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {formData.spec_invasiva === "si" && (
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">Specializzazioni INVASIVE / CHIRURGICHE</Label>
          <Select value={formData.spec_inv || ""} onValueChange={v => update("spec_inv", v)}>
            <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="san-spec-inv"><SelectValue placeholder="Seleziona specializzazione" /></SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {specInv.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  const renderCondizioni = () => (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Massimale richiesto (per sinistro / aggregato annuo) <span className="text-[#D97B58]">*</span></Label>
        <Select value={formData.massimale_richiesto || ""} onValueChange={v => update("massimale_richiesto", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="san-massimale"><SelectValue placeholder="Seleziona massimale" /></SelectTrigger>
          <SelectContent>
            {MASSIMALI.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Retroattivit&agrave; (minimo 10 anni) <span className="text-[#D97B58]">*</span></Label>
        <Select value={formData.retroattivita || ""} onValueChange={v => update("retroattivita", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="san-retroattivita"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            {RETROATTIVITA.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Franchigia richiesta (per sinistro) <span className="text-[#D97B58]">*</span></Label>
        <Select value={formData.franchigia_richiesta || ""} onValueChange={v => update("franchigia_richiesta", v)}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11" data-testid="san-franchigia"><SelectValue placeholder="Seleziona franchigia" /></SelectTrigger>
          <SelectContent>
            {FRANCHIGIE.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <YesNoField label="Tacito Rinnovo?" required field="tacito_rinnovo" formData={formData} update={update} testId="san-tacito-rinnovo" />
    </div>
  );

  const renderEstensioni = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51] mb-2">Rispondi alle seguenti domande relative alle estensioni di copertura.</p>
      <YesNoField label="Prestatori d'opera con obbligo nei contratti assicurativi (art. 4 c.4 D. 232/2023)?" required field="prestatori_opera" formData={formData} update={update} testId="san-prestatori" hint="Obbligatorio se ti avvali di dipendenti o collaboratori per eseguire l'attività" />
      <YesNoField label="Attività libero professionale solo per strutture, senza obbligazione contrattuale diretta con il cliente?" required field="att_solo_strutture" formData={formData} update={update} testId="san-att-strutture" />
      <YesNoField label="Ha lavorato come libero professionista e/o convenzionato presso strutture sanitarie private prima del 01/04/2017?" required field="lavorato_pre_2017" formData={formData} update={update} testId="san-pre-2017" />
      <YesNoField label="Ha adempiuto ad un obbligo contrattuale verso un paziente (art. 7 comma 3, Legge n. 24/2017)?" required field="obbligo_contrattuale" formData={formData} update={update} testId="san-obbligo" />
      <YesNoField label="Notifica fatti/circostanze che possono generare sinistri?" required field="notifica_fatti" formData={formData} update={update} testId="san-notifica" />
      <YesNoField label="Inadeguatezza/insufficienza del Consenso informato? (estensione)" required field="consenso_informato" formData={formData} update={update} testId="san-consenso" />
      <YesNoField label="Attività Invasiva Lieve (per specializzazioni NON chirurgiche)?" required field="att_invasiva_lieve" formData={formData} update={update} testId="san-invasiva-lieve" />
      <YesNoField label="Medicina Estetica Light (per specializzazioni NON invasive)?" required field="estetica_light" formData={formData} update={update} testId="san-estetica-light" />
      <YesNoField label="Spese Legali - Procedimenti Penali (art. 6.1)?" required field="spese_legali" formData={formData} update={update} testId="san-spese-legali" />
      <YesNoField label="Attività come Dirigente Medico II Livello / Direttore Sanitario?" required field="dirigente_medico" formData={formData} update={update} testId="san-dirigente" />

      {(formData.dirigente_medico === "si" || selectedCategory === "struttura") && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <FieldInput label="Ragione Sociale Struttura" required value={formData.ragione_sociale_struttura} onChange={v => update("ragione_sociale_struttura", v)} testId="san-rag-soc-struttura" />
          <FieldInput label="Numero Posti Letto" type="number" required value={formData.posti_letto} onChange={v => update("posti_letto", v)} testId="san-posti-letto" />
          <FieldInput label="Aree di specializzazione della Struttura e n° procedure annue" value={formData.aree_spec_struttura} onChange={v => update("aree_spec_struttura", v)} testId="san-aree-spec" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldInput label="Retribuzioni Totali (stima esercizio corrente)" required value={formData.retribuzioni_corrente} onChange={v => update("retribuzioni_corrente", v)} testId="san-retrib-corrente" />
            <FieldInput label={enrichLabelWithYear("Retribuzioni Totali (ultimo esercizio)")} required value={formData.retribuzioni_ultimo} onChange={v => update("retribuzioni_ultimo", v)} testId="san-retrib-ultimo" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldInput label={enrichLabelWithYear("Fatturato Totale (ultimo esercizio)")} required value={formData.fatturato_ultimo} onChange={v => update("fatturato_ultimo", v)} testId="san-fatturato" />
            <FieldInput label="Emolumenti annui percepiti" required value={formData.emolumenti} onChange={v => update("emolumenti", v)} testId="san-emolumenti" />
          </div>
        </div>
      )}
    </div>
  );

  const handleClaimsChange = (updatedClaims) => {
    update("sinistri_claims", updatedClaims);
  };

  const renderSinistri = () => (
    <div className="space-y-5">
      <YesNoField label="Richieste di risarcimento / azioni responsabilità negli ultimi 5 anni?" required field="sinistri_5_anni" formData={formData} update={update} testId="san-sinistri-5" />
      <YesNoField label="A conoscenza di fatti da cui possano derivare richieste di risarcimento?" required field="fatti_risarcimento" formData={formData} update={update} testId="san-fatti-risc" />

      {showClaimsSheet && formData.sinistri_claims && formData.sinistri_claims.length > 0 && (
        <div className="mt-4 p-4 sm:p-5 bg-amber-50/50 border border-amber-200/60 rounded-2xl space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            In caso di risposta affermativa, compilare il <strong>Modulo CIS</strong> sottostante per ciascun sinistro o circostanza.
          </div>
          <ClaimsInfoSheet
            claims={formData.sinistri_claims}
            onClaimsChange={handleClaimsChange}
          />
        </div>
      )}

      <FileUploadZone
        label="Upload documenti aggiuntivi"
        hint="Carica eventuali documenti rilevanti a supporto (perizie, lettere, atti)"
        required={false}
        onFileUploaded={(data) => handleFileUpload("modulo_cis", data)}
      />

      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Note aggiuntive</Label>
        <Textarea value={formData.dettagli_sinistri || ""} onChange={e => update("dettagli_sinistri", e.target.value)}
          className="rounded-xl border-[#E5E2DB] focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321] min-h-[100px]"
          placeholder="Eventuali ulteriori dettagli o circostanze rilevanti..."
          data-testid="san-dettagli-sinistri" />
      </div>
    </div>
  );

  const renderDichiarazioni = () => (
    <div className="space-y-6">
      <p className="text-sm text-[#4A5E51]">
        Prima di inviare la richiesta, conferma di aver preso visione dei seguenti documenti.
      </p>

      <DichiarazioniBlock formData={formData} update={update} />

      <div className="flex items-start gap-3 p-4 bg-[#F5F4F0] rounded-xl">
        <Checkbox id="set_informativo" checked={formData.set_informativo || false} onCheckedChange={v => update("set_informativo", v)} className="mt-0.5 data-[state=checked]:bg-[#163321]" data-testid="san-set-info-checkbox" />
        <label htmlFor="set_informativo" className="text-xs text-[#4A5E51] leading-relaxed cursor-pointer">
          <strong>Set Informativo</strong> <span className="text-[#D97B58]">*</span><br />
          In ottemperanza all'art. 27 del Regolamento IVASS n. 41/2018, dichiaro di aver ricevuto e preso visione del set informativo del prodotto assicurativo.
        </label>
      </div>

      <div className="p-4 bg-[#163321]/5 rounded-xl border border-[#163321]/10">
        <p className="text-xs text-[#4A5E51] leading-relaxed">
          <strong>Nota:</strong> La garanzia "Responsabilit&agrave; solidale" &egrave; sempre inclusa ai sensi della Legge 8 Marzo 2017, n.24.
          L'Azione di Rivalsa anticipata in sede civile per Colpa Grave &egrave; sempre inclusa ai sensi della stessa legge.
        </p>
      </div>
    </div>
  );

  const canSubmit = formData.nome && formData.cognome && formData.email && formData.telefono && formData.privacy;

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="sanitario-form-page">
        <div className="max-w-2xl mx-auto px-6 py-12">
          {/* Title */}
          <div className="text-center mb-8">
            <button onClick={() => { setSelectedCategory(null); setStep(0); setFormData({}); setFiles({}); }} className="text-xs text-[#D97B58] hover:underline mb-2 inline-block" data-testid="back-to-categories">
              &larr; Cambia categoria
            </button>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="sanitario-form-title">
              RC Professioni Sanitarie
            </h1>
            <p className="text-sm text-[#4A5E51]">{categoryLabel}</p>
          </div>

          {/* Progress */}
          <StepProgress steps={STEPS} currentStep={step} onStepClick={setStep} progress={progress} testIdPrefix="san" />

          {/* Step content */}
          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 mb-6 shadow-sm" data-testid={`san-step-content-${step}`}>
            <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">{STEPS[step]}</h2>
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <FormNavigation
            step={step} totalSteps={totalSteps}
            onPrev={() => step === 0 ? setSelectedCategory(null) : setStep(s => s - 1)}
            onNext={() => setStep(s => s + 1)}
            onSubmit={handleSubmit} submitting={submitting} canSubmit={canSubmit}
            prevLabel={step === 0 ? "Categorie" : "Indietro"} testIdPrefix="san"
          />
        </div>
        <PrivacyLinks className="mt-6 mb-4" />
      </main>
      <Footer />
    </>
  );
}

// --- Reusable field components ---

function FieldInput({ label, required, type = "text", value, onChange, placeholder, testId, hint }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-[#0F1F14]">
        {label} {required && <span className="text-[#D97B58]">*</span>}
      </Label>
      {hint && <p className="text-[10px] text-[#4A5E51]">{hint}</p>}
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
        className="rounded-xl border-[#E5E2DB] h-11 focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321]"
        placeholder={placeholder} data-testid={testId} />
    </div>
  );
}

function YesNoField({ label, required, field, formData, update, testId, hint }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-[#0F1F14]">
        {label} {required && <span className="text-[#D97B58]">*</span>}
      </Label>
      {hint && <p className="text-[10px] text-[#4A5E51]">{hint}</p>}
      <RadioGroup value={formData[field] || ""} onValueChange={v => update(field, v)} className="flex gap-4">
        <div className="flex items-center gap-2">
          <RadioGroupItem value="si" id={`${field}-si`} data-testid={`${testId}-si`} />
          <Label htmlFor={`${field}-si`} className="text-sm cursor-pointer">S&igrave;</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="no" id={`${field}-no`} data-testid={`${testId}-no`} />
          <Label htmlFor={`${field}-no`} className="text-sm cursor-pointer">No</Label>
        </div>
      </RadioGroup>
    </div>
  );
}
